import { useCallback, useEffect, useMemo, useRef } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Box from '@mui/material/Box';

import { useGetDaasProjects } from '../../../hooks';
import { ApplicationBasicInfoSection } from './sections/ApplicationBasicInfoSection';
import { ApplicationClusterSection } from './sections/ApplicationClusterSection';
import { ApplicationEditBasicInfoSection } from './sections/ApplicationEditBasicInfoSection';
import { ApplicationSourceSection } from './sections/ApplicationSourceSection';
import { ApplicationSyncOptionsSection } from './sections/ApplicationSyncOptionsSection';
import { AutoNamespaceSection } from './sections/AutoNamespaceSection';
import { AutoSyncSection } from './sections/AutoSyncSection';
import { ImageUpdaterSection } from './sections/ImageUpdaterSection';
import { MetadataSection } from './sections/MetadataSection';
import { OwnerSection } from './sections/OwnerSection';
import { ProjectSection } from './sections/ProjectSection';
import { useStyles } from './styles';
import {
  ApplicationBasicInfoValue,
  APPLICATION_DEFAULT_DATA,
  AutoCreateNamespaceConfig,
  AutoSyncConfig,
  Cluster,
  ClusterConfiguration,
  DaasApplicationInputData,
  DaasApplicationInputUiOptions,
  DEFAULT_IMAGE_UPDATER_CONFIG,
  ImageUpdaterConfig,
  KubernetesMetadata,
  SourceConfiguration,
  SyncOption,
} from './types';
import { serializeProjectEntity } from './utils';


const withSeededKeys = (
  data: DaasApplicationInputData,
): DaasApplicationInputData => ({
  ...data,
  syncPolicy: data.syncPolicy ?? APPLICATION_DEFAULT_DATA.syncPolicy,
  applicationMetadata:
    data.applicationMetadata ?? APPLICATION_DEFAULT_DATA.applicationMetadata,
  imageUpdater: data.imageUpdater ?? DEFAULT_IMAGE_UPDATER_CONFIG,
});

export const DaasApplicationInput = (
  props: FieldExtensionComponentProps<
    DaasApplicationInputData,
    DaasApplicationInputUiOptions
  >,
) => {
  const { onChange, formData, uiSchema, rawErrors } = props;
  const { classes } = useStyles();
  const fillEditFormData = uiSchema?.['ui:options']?.fillEditFormData === true;

  // Stabilize rawErrors — Backstage creates a new array reference on every render
  // even when contents are unchanged (typically []). Without this, React.memo on
  // sections is defeated because every section receives a new errors prop each render.
  const stableErrorsRef = useRef<string[]>(rawErrors ?? []);
  const nextErrors = rawErrors ?? [];
  if (
    nextErrors.length !== stableErrorsRef.current.length ||
    nextErrors.some((e, i) => e !== stableErrorsRef.current[i])
  ) {
    stableErrorsRef.current = nextErrors;
  }
  const errors = stableErrorsRef.current;

  const data = formData ?? APPLICATION_DEFAULT_DATA;
  const syncPolicy = useMemo(
    () => data.syncPolicy ?? APPLICATION_DEFAULT_DATA.syncPolicy,
    [data.syncPolicy],
  );

  // Refs to latest values — allow stable callbacks without stale closures
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;
  const syncPolicyRef = useRef(syncPolicy);
  syncPolicyRef.current = syncPolicy;

  const { value: entities, loading } = useGetDaasProjects(false, true);
  const userProjects = useMemo(
    () => entities?.map(entity => serializeProjectEntity(entity)) || [],
    [entities],
  );
  const selectedProject = useMemo(
    () => userProjects.find(project => project.name === data.project),
    [userProjects, data.project],
  );
  const projectClusters: Cluster[] = selectedProject?.clusters ?? [];

  const updateData = useCallback((patch: Partial<DaasApplicationInputData>) => {
    onChangeRef.current(withSeededKeys({ ...dataRef.current, ...patch }));
  }, []);

  const updateSyncPolicy = useCallback(
    (patch: Partial<DaasApplicationInputData['syncPolicy']>) => {
      onChangeRef.current(
        withSeededKeys({
          ...dataRef.current,
          syncPolicy: { ...syncPolicyRef.current, ...patch },
        }),
      );
    },
    [],
  );

  const handleProjectChange = useCallback(
    (project: string) => {
      updateData({
        project,
        cluster: null,
        source: null,
      });
    },
    [updateData],
  );

  const handleBasicInfoChange = useCallback(
    (v: ApplicationBasicInfoValue) => {
      updateData(v);
    },
    [updateData],
  );

  const handleEditBasicInfoChange = useCallback(
    (
      v: Pick<
        DaasApplicationInputData,
        'applicationName' | 'description' | 'applicationRef'
      >,
    ) => {
      updateData(v);
    },
    [updateData],
  );

  // `_initialized` has to travel in formData: the scaffolder unmounts and
  // remounts field components as the user steps through the form, so a ref, a
  // context or sessionStorage would not survive to stop a second prefill from
  // discarding the user's edits.
  const handleApplicationEntitySelect = useCallback(
    (fullData: Partial<DaasApplicationInputData>) => {
      const sanitizedData = Object.fromEntries(
        Object.entries(fullData).filter(([, value]) => value !== undefined),
      ) as Partial<DaasApplicationInputData>;
      updateData({ ...sanitizedData, _initialized: true });
    },
    [updateData],
  );

  const handleOwnerChange = useCallback(
    (v: string) => {
      updateData({ owner: v });
    },
    [updateData],
  );

  const handleClusterChange = useCallback(
    (v: ClusterConfiguration | null) => {
      updateData({ cluster: v });
    },
    [updateData],
  );

  const handleSourceChange = useCallback(
    (v: SourceConfiguration | null) => {
      updateData({ source: v });
    },
    [updateData],
  );

  const handleAutoSyncChange = useCallback(
    (v: AutoSyncConfig) => {
      updateSyncPolicy({ autoSync: v });
    },
    [updateSyncPolicy],
  );

  const handleAutoNamespaceChange = useCallback(
    (v: AutoCreateNamespaceConfig) => {
      updateSyncPolicy({ autoCreateNamespace: v });
    },
    [updateSyncPolicy],
  );

  const handleSyncOptionsChange = useCallback(
    (v: SyncOption[]) => {
      updateSyncPolicy({ syncOptions: v });
    },
    [updateSyncPolicy],
  );

  const handleMetadataChange = useCallback(
    (v: KubernetesMetadata) => {
      updateData({ applicationMetadata: v });
    },
    [updateData],
  );

  const handleImageUpdaterChange = useCallback(
    (v: ImageUpdaterConfig) => {
      updateData({ imageUpdater: v });
    },
    [updateData],
  );

  // Initialize syncPolicy once on mount so downstream catalog actions always
  // receive a fully structured object
  useEffect(() => {
    const d = dataRef.current;
    if (!d.syncPolicy || !d.applicationMetadata || !d.imageUpdater) {
      onChangeRef.current({
        ...d,
        syncPolicy: d.syncPolicy || APPLICATION_DEFAULT_DATA.syncPolicy,
        applicationMetadata:
          d.applicationMetadata || APPLICATION_DEFAULT_DATA.applicationMetadata,
        imageUpdater: d.imageUpdater || DEFAULT_IMAGE_UPDATER_CONFIG,
      });
    }
  }, []);

  // Memoized value slices — sections only re-render when their own data changes
  const basicInfoValue = useMemo(
    () => ({
      applicationName: data.applicationName ?? '',
      description: data.description ?? '',
    }),
    [data.applicationName, data.description],
  );

  const editBasicInfoValue = useMemo(
    () => ({
      applicationName: data.applicationName ?? '',
      description: data.description ?? '',
      applicationRef: data.applicationRef,
    }),
    [data.applicationName, data.description, data.applicationRef],
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.mainContainer}>
        <ProjectSection
          value={data.project}
          projects={userProjects}
          loading={loading}
          onChange={handleProjectChange}
          errors={errors}
          disabled={fillEditFormData}
        />
        {fillEditFormData ? (
          <ApplicationEditBasicInfoSection
            value={editBasicInfoValue}
            onChange={handleEditBasicInfoChange}
            onApplicationEntitySelect={handleApplicationEntitySelect}
            isFormAlreadyPopulated={!!data._initialized}
            errors={errors}
          />
        ) : (
          <ApplicationBasicInfoSection
            value={basicInfoValue}
            selectedProject={data.project}
            onChange={handleBasicInfoChange}
            errors={errors}
          />
        )}
        {fillEditFormData && (
          <OwnerSection
            value={data.owner ?? ''}
            onChange={handleOwnerChange}
            errors={errors}
          />
        )}
        <ApplicationClusterSection
          value={data.cluster}
          availableClusters={projectClusters}
          onChange={handleClusterChange}
          errors={errors}
        />
        <ApplicationSourceSection
          value={data.source}
          onChange={handleSourceChange}
          errors={errors}
        />
        <AutoSyncSection
          value={syncPolicy.autoSync}
          onChange={handleAutoSyncChange}
        />
        <AutoNamespaceSection
          value={syncPolicy.autoCreateNamespace}
          onChange={handleAutoNamespaceChange}
          errors={errors}
        />
        <ApplicationSyncOptionsSection
          value={syncPolicy.syncOptions}
          onChange={handleSyncOptionsChange}
          errors={errors}
        />
        <MetadataSection
          value={data.applicationMetadata}
          onChange={handleMetadataChange}
          errors={errors}
        />
        <ImageUpdaterSection
          value={data.imageUpdater ?? DEFAULT_IMAGE_UPDATER_CONFIG}
          onChange={handleImageUpdaterChange}
          source={data.source}
          errors={errors}
        />
      </Box>
    </Box>
  );
};
